import type { DataAudit, DataAuditIssue, IssueEntityType, IssueSeverity, IssueLink } from '@cls/shared';
import { CompSection } from '../CompSection.js';

const SEV_ICON: Record<IssueSeverity, string> = { critical: '✗', warn: '⚠', info: 'ℹ' };
const SEV_COLOR: Record<IssueSeverity, string> = {
  critical: 'var(--danger)',
  warn: 'var(--warn)',
  info: 'var(--muted)',
};
const SEV_BG: Record<IssueSeverity, string> = {
  critical: 'var(--danger-dim)',
  warn: 'var(--warn-dim)',
  info: 'rgba(107,122,153,.1)',
};
const SEV_LABEL: Record<IssueSeverity, string> = {
  critical: 'Critical',
  warn: 'Warning',
  info: 'Info',
};
const TYPE_ICON: Record<IssueEntityType, string> = {
  Worker: '👷',
  Project: '🏗',
  Contact: '📇',
  Customer: '🏢',
  Supplier: '📦',
};
const TYPE_ORDER: IssueEntityType[] = ['Worker', 'Project', 'Contact', 'Customer', 'Supplier'];

const SEV_RANK: Record<IssueSeverity, number> = { critical: 0, warn: 1, info: 2 };

interface Props {
  audit: DataAudit;
  onIssueClick: (link: IssueLink) => void;
}

export function DataQualitySection({ audit, onIssueClick }: Props) {
  if (audit.total === 0) {
    return (
      <CompSection title="📋 Data Quality — Missing Information (0)" type="info">
        <div style={{ color: 'var(--live)', padding: '10px 14px' }}>
          ✓ All records are complete — no missing information found
        </div>
      </CompSection>
    );
  }

  const headerType: 'danger' | 'warn' = audit.totalCritical > 0 ? 'danger' : 'warn';
  const headerColor = headerType === 'danger' ? 'var(--danger)' : 'var(--warn)';
  const headerBg = headerType === 'danger' ? 'var(--danger-dim)' : 'var(--warn-dim)';

  return (
    <div
      style={{
        background: 'var(--card)',
        border: '1px solid var(--border)',
        borderRadius: 12,
        overflow: 'hidden',
        marginBottom: 12,
      }}
    >
      <div
        style={{
          padding: '11px 15px',
          background: headerBg,
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div
          style={{
            fontFamily: 'Syne, sans-serif',
            fontWeight: 700,
            fontSize: 12,
            color: headerColor,
          }}
        >
          {`📋 Data Quality — Missing Information (${audit.total})`}
        </div>
        <div style={{ fontSize: 10, color: 'var(--muted)' }}>
          {`${audit.totalCritical} critical · ${audit.totalWarn} warnings · ${audit.totalInfo} info`}
        </div>
      </div>
      <div style={{ padding: 12 }}>
        <SummaryGrid audit={audit} />
        {TYPE_ORDER.map((type) => {
          const items = audit.byType[type];
          if (!items || items.length === 0) return null;
          return <TypeTable key={type} type={type} items={items} onIssueClick={onIssueClick} />;
        })}
      </div>
    </div>
  );
}

function SummaryGrid({ audit }: { audit: DataAudit }) {
  const cells = [
    { label: 'Critical', value: audit.totalCritical, color: 'var(--danger)', bg: 'var(--danger-dim)' },
    { label: 'Warnings', value: audit.totalWarn, color: 'var(--warn)', bg: 'var(--warn-dim)' },
    { label: 'Informational', value: audit.totalInfo, color: 'var(--muted)', bg: 'rgba(107,122,153,.1)' },
    { label: 'Total Issues', value: audit.total, color: 'var(--text)', bg: 'var(--card)' },
  ];
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
        gap: 8,
        marginBottom: 14,
      }}
    >
      {cells.map((c) => (
        <div
          key={c.label}
          style={{
            background: c.bg,
            border: '1px solid rgba(107,122,153,.25)',
            borderRadius: 8,
            padding: 10,
            textAlign: 'center',
          }}
        >
          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 20, color: c.color }}>{c.value}</div>
          <div
            style={{
              fontSize: 9,
              color: c.color,
              textTransform: 'uppercase',
              letterSpacing: '.6px',
              marginTop: 2,
            }}
          >
            {c.label}
          </div>
        </div>
      ))}
    </div>
  );
}

function TypeTable({
  type,
  items,
  onIssueClick,
}: {
  type: IssueEntityType;
  items: DataAuditIssue[];
  onIssueClick: (link: IssueLink) => void;
}) {
  const critCount = items.filter((i) => i.severity === 'critical').length;
  const titleCol = critCount > 0 ? 'var(--danger)' : 'var(--warn)';
  const titleBg = critCount > 0 ? 'var(--danger-dim)' : 'var(--warn-dim)';
  const sorted = [...items].sort((a, b) => (SEV_RANK[a.severity] ?? 9) - (SEV_RANK[b.severity] ?? 9));

  return (
    <div
      style={{
        background: 'var(--card)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        overflow: 'hidden',
        marginBottom: 10,
      }}
    >
      <div
        style={{
          padding: '9px 14px',
          background: titleBg,
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <span style={{ fontSize: 14 }}>{TYPE_ICON[type]}</span>
        <span
          style={{
            fontFamily: 'Syne, sans-serif',
            fontWeight: 700,
            fontSize: 11,
            color: titleCol,
          }}
        >
          {`${type}s — ${items.length} missing field${items.length !== 1 ? 's' : ''}`}
        </span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ margin: 0 }}>
          <thead>
            <tr>
              <th>{type}</th>
              <th>Missing Field</th>
              <th>Severity</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((i, idx) => (
              <tr key={`${i.id}-${i.field}-${idx}`}>
                <td
                  style={{ cursor: 'pointer', color: 'var(--accent)', fontWeight: 500 }}
                  onClick={() => onIssueClick(i.link)}
                >
                  {i.entity}
                </td>
                <td>{i.field}</td>
                <td>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 10,
                      padding: '2px 7px',
                      borderRadius: 5,
                      background: SEV_BG[i.severity],
                      color: SEV_COLOR[i.severity],
                      fontWeight: 600,
                    }}
                  >
                    {`${SEV_ICON[i.severity]} ${SEV_LABEL[i.severity]}`}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

